import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';
import AdminApplications from './AdminApplications';
import AdminOnboardingApplications from './AdminOnboardingApplications';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://vsoomqfayfmtbnulnugn.supabase.co';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:    '#0A1628', gold:    '#C9A84C', black:   '#0A0A0A',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger:  '#C0222A', amber:   '#92640A',
  teal:    '#0D6E8A',
};

interface AdminProfile {
  id: string; email: string; full_name: string;
  role: 'allocator' | 'manager';
  account_status: 'pending_review' | 'approved' | 'rejected' | 'suspended';
  submitted_at: string | null; reviewed_at: string | null;
  rejection_reason: string | null; admin_notes: string | null;
  is_super_admin: boolean;
  firm?: { id: string; name: string; type: string; headquarters: string; aum_min: number; aum_max: number; description: string; website: string; } | null;
}
interface WaitlistEntry {
  id: string; email: string; role_interest: 'manager' | 'allocator' | null;
  source: string; firm_name: string | null; message: string | null;
  status: 'pending' | 'invited' | 'registered' | 'declined';
  notes: string | null; created_at: string; invited_at: string | null;
  registered_at: string | null; user_agent: string | null;
}
interface Invitation {
  id: string; email: string; invited_by: string;
  role_intended: 'manager' | 'allocator'; firm_name_suggested: string | null;
  firm_id: string | null; custom_message: string | null; token: string;
  status: 'sent' | 'opened' | 'accepted' | 'expired' | 'revoked';
  sent_at: string; opened_at: string | null; accepted_at: string | null;
  expires_at: string; inviter?: { full_name: string };
}
type AdminTab = 'pending' | 'approved' | 'rejected' | 'suspended' | 'all' | 'waitlist' | 'invitations' | 'applications';

// Guards the ?tab= value so a bad URL falls back to the default rather than
// querying profiles for a non-existent account_status.
const ADMIN_TABS: AdminTab[] = ['pending', 'approved', 'rejected', 'suspended', 'all', 'waitlist', 'invitations', 'applications'];

// ── Shared styles ─────────────────────────────────────────────────────────────
const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden', marginBottom: '12px' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '3px', background: C.white, color: C.text, border: `1px solid ${C.border}`, fontSize: '13px', marginBottom: '12px', boxSizing: 'border-box', fontFamily: 'system-ui', outline: 'none' };
const textareaStyle: React.CSSProperties = { ...inputStyle, minHeight: '80px', resize: 'vertical' as const, marginBottom: '12px' };
const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.7px', textTransform: 'uppercase' as const, display: 'block', marginBottom: '5px', fontFamily: 'system-ui' };

function statusBadge(status: string): React.CSSProperties {
  const base: React.CSSProperties = { fontSize: '9px', padding: '3px 8px', borderRadius: '2px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.5px', fontFamily: 'system-ui' };
  if (['pending_review','pending','sent'].includes(status)) return { ...base, background: '#FFFBEB', color: C.amber, border: '1px solid #FDE68A' };
  if (['approved','registered','accepted'].includes(status)) return { ...base, background: '#F0FDF4', color: C.success, border: '1px solid #BBF7D0' };
  if (['rejected','declined','revoked','expired','suspended'].includes(status)) return { ...base, background: '#FEF2F2', color: C.danger, border: '1px solid #FECACA' };
  if (['invited','opened'].includes(status)) return { ...base, background: '#EFF8FB', color: C.teal, border: `1px solid ${C.teal}30` };
  return base;
}

const btnPrimary: React.CSSProperties = { background: C.navy, color: C.white, border: 'none', padding: '8px 16px', borderRadius: '3px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui', letterSpacing: '0.2px' };
const btnDanger: React.CSSProperties = { background: C.white, color: C.danger, border: `1px solid ${C.danger}`, padding: '8px 16px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui' };
const btnSecondary: React.CSSProperties = { background: C.white, color: C.textSec, border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui' };

export default function Admin() {
  const { profile: myProfile, loading: profileLoading } = useProfile();
  // The tab lives in the URL so the sidebar can deep-link straight to
  // /admin?tab=applications. 'pending' is the param-less default.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as AdminTab | null;
  const activeTab: AdminTab = tabParam && ADMIN_TABS.includes(tabParam) ? tabParam : 'pending';
  const setActiveTab = (t: AdminTab) =>
    setSearchParams(t === 'pending' ? {} : { tab: t });
  const [users, setUsers]                   = useState<AdminProfile[]>([]);
  const [waitlist, setWaitlist]             = useState<WaitlistEntry[]>([]);
  const [invitations, setInvitations]       = useState<Invitation[]>([]);
  const [loading, setLoading]               = useState(true);
  const [selectedUser, setSelectedUser]     = useState<AdminProfile | null>(null);
  const [selectedWaitlist, setSelectedWaitlist] = useState<WaitlistEntry | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editNotes, setEditNotes]           = useState('');
  const [editRejectionReason, setEditRejectionReason] = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [inviteEmail, setInviteEmail]       = useState('');
  const [inviteRole, setInviteRole]         = useState<'manager' | 'allocator'>('manager');
  const [inviteFirmName, setInviteFirmName] = useState('');
  const [inviteMessage, setInviteMessage]   = useState('');
  const [inviteResult, setInviteResult]     = useState<{ success: boolean; message: string } | null>(null);

  const pendingCount         = useMemo(() => users.filter(u => u.account_status === 'pending_review').length, [users]);
  const waitlistPendingCount = useMemo(() => waitlist.filter(w => w.status === 'pending').length, [waitlist]);
  const invitationsSentCount = useMemo(() => invitations.filter(i => ['sent','opened'].includes(i.status)).length, [invitations]);

  useEffect(() => { if (!profileLoading && myProfile?.is_super_admin) loadAll(); }, [activeTab, myProfile?.is_super_admin, profileLoading]);

  const loadAll = async () => {
    // The Applications tab owns its own data loading.
    if (activeTab === 'applications') { setLoading(false); return; }
    setLoading(true);
    if (activeTab === 'waitlist') {
      const { data } = await supabase.from('waitlist').select('*').order('created_at', { ascending: false });
      setWaitlist((data || []) as any);
    } else if (activeTab === 'invitations') {
      const { data } = await supabase.from('invitations').select('*, inviter:profiles!invitations_invited_by_fkey(full_name)').order('sent_at', { ascending: false });
      setInvitations((data || []) as any);
    } else {
      let query = supabase.from('profiles').select('*, firms!firms_owner_id_fkey(id, name, type, headquarters, aum_min, aum_max, description, website)').order('submitted_at', { ascending: false, nullsFirst: false });
      if (activeTab !== 'all') query = query.eq('account_status', activeTab);
      const { data } = await query;
      setUsers((data || []).map((p: any) => ({ ...p, firm: p.firms?.length > 0 ? p.firms[0] : null })));
    }
    setLoading(false);
  };

  const closeModal = () => {
    setSelectedUser(null); setSelectedWaitlist(null); setSelectedInvitation(null);
    setShowInviteModal(false); setEditNotes(''); setEditRejectionReason('');
    setInviteEmail(''); setInviteRole('manager'); setInviteFirmName('');
    setInviteMessage(''); setInviteResult(null);
  };

  const openUserModal = (u: AdminProfile) => { setSelectedUser(u); setEditNotes(u.admin_notes || ''); setEditRejectionReason(u.rejection_reason || ''); };
  const openWaitlistModal = (w: WaitlistEntry) => { setSelectedWaitlist(w); setEditNotes(w.notes || ''); };

  const updateProfile = async (updates: object) => {
    if (!selectedUser || !myProfile) return false;
    setActionLoading(true);
    const { error } = await supabase.from('profiles').update({ ...updates, reviewed_at: new Date().toISOString(), reviewed_by: myProfile.id }).eq('id', selectedUser.id);
    setActionLoading(false);
    if (error) { alert(error.message); return false; }
    closeModal(); await loadAll(); return true;
  };

  const handleApprove   = () => updateProfile({ account_status: 'approved', admin_notes: editNotes || null, rejection_reason: null });
  const handleReject    = () => { if (!editRejectionReason.trim()) { alert('Please provide a rejection reason.'); return; } updateProfile({ account_status: 'rejected', admin_notes: editNotes || null, rejection_reason: editRejectionReason.trim() }); };
  const handleSuspend   = () => { if (window.confirm(`Suspend ${selectedUser?.full_name}?`)) updateProfile({ account_status: 'suspended', admin_notes: editNotes || null }); };
  const handleReinstate = () => updateProfile({ account_status: 'approved', admin_notes: editNotes || null, rejection_reason: null });
  const handleSaveNotes = async () => { if (!selectedUser) return; setActionLoading(true); await supabase.from('profiles').update({ admin_notes: editNotes || null }).eq('id', selectedUser.id); setActionLoading(false); closeModal(); await loadAll(); };

  const updateWaitlist = async (updates: object) => {
    if (!selectedWaitlist) return;
    setActionLoading(true);
    const { error } = await supabase.from('waitlist').update(updates).eq('id', selectedWaitlist.id);
    setActionLoading(false);
    if (error) { alert(error.message); return; }
    closeModal(); await loadAll();
  };
  const handleMarkInvited     = () => updateWaitlist({ status: 'invited', invited_at: new Date().toISOString(), invited_by: myProfile?.id, notes: editNotes || null });
  const handleMarkDeclined    = () => { if (window.confirm(`Decline "${selectedWaitlist?.email}"?`)) updateWaitlist({ status: 'declined', notes: editNotes || null }); };
  const handleReopenWaitlist  = () => updateWaitlist({ status: 'pending', invited_at: null, notes: editNotes || null });
  const handleSaveWaitlistNotes = () => updateWaitlist({ notes: editNotes || null });
  const handleDeleteWaitlist  = async () => { if (!selectedWaitlist || !window.confirm(`Delete "${selectedWaitlist.email}"? Cannot be undone.`)) return; setActionLoading(true); await supabase.from('waitlist').delete().eq('id', selectedWaitlist.id); setActionLoading(false); closeModal(); await loadAll(); };

  const handleSendInvitation = async (waitlistEntry?: WaitlistEntry) => {
    setActionLoading(true); setInviteResult(null);
    const email = waitlistEntry ? waitlistEntry.email : inviteEmail.trim();
    const role = waitlistEntry?.role_interest || inviteRole;
    if (!email || !role) { alert('Email and role are required.'); setActionLoading(false); return; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-invitation-email`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role_intended: role, firm_name_suggested: waitlistEntry?.firm_name || inviteFirmName.trim() || null, custom_message: inviteMessage.trim() || null }),
      });
      const json = await res.json();
      if (!res.ok) { setInviteResult({ success: false, message: json.error || 'Failed to send' }); }
      else {
        setInviteResult({ success: true, message: `Invitation sent to ${email}.` });
        if (waitlistEntry && myProfile) { await supabase.from('waitlist').update({ status: 'invited', invited_at: new Date().toISOString(), invited_by: myProfile.id }).eq('id', waitlistEntry.id); closeModal(); }
        await loadAll();
      }
    } catch (err: any) { setInviteResult({ success: false, message: err.message }); }
    setActionLoading(false);
  };

  const handleRevokeInvitation = async () => {
    if (!selectedInvitation || !window.confirm(`Revoke invitation to ${selectedInvitation.email}?`)) return;
    setActionLoading(true);
    await supabase.from('invitations').update({ status: 'revoked' }).eq('id', selectedInvitation.id);
    setActionLoading(false); closeModal(); await loadAll();
  };

  const handleResendInvitation = async () => {
    if (!selectedInvitation) return;
    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-invitation-email`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: selectedInvitation.email, role_intended: selectedInvitation.role_intended, firm_name_suggested: selectedInvitation.firm_name_suggested, custom_message: selectedInvitation.custom_message }),
      });
      const json = await res.json();
      if (!res.ok) alert(`Resend failed: ${json.error || 'Unknown error'}`);
      else { alert(`Invitation resent to ${selectedInvitation.email}.`); closeModal(); await loadAll(); }
    } catch (err: any) { alert(`Resend failed: ${err.message}`); }
    setActionLoading(false);
  };

  const copyInviteUrl = (token: string) => { navigator.clipboard.writeText(`https://app.altveno.com/invite/${token}`); alert('Copied to clipboard'); };

  const fmt = (ts: string) => new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const fmtFull = (ts: string) => new Date(ts).toLocaleString('en-GB');

  if (profileLoading) return <div style={{ padding: '32px', background: C.surface, minHeight: '100vh' }}><div style={{ color: C.muted, fontSize: '13px' }}>Loading...</div></div>;
  if (!myProfile?.is_super_admin) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui' }}>
      <div style={{ fontSize: '16px', fontWeight: 600, color: C.text }}>Access denied</div>
      <div style={{ fontSize: '13px', color: C.muted, marginTop: '4px' }}>You do not have permission to view this page.</div>
    </div>
  );

  const tabs: { key: AdminTab; label: string; badge?: number }[] = [
    { key: 'pending',     label: 'Pending Review', badge: pendingCount || undefined },
    { key: 'approved',    label: 'Approved'        },
    { key: 'rejected',    label: 'Rejected'        },
    { key: 'suspended',   label: 'Suspended'       },
    { key: 'all',         label: 'All Users'       },
    { key: 'waitlist',    label: 'Waitlist',        badge: waitlistPendingCount || undefined },
    { key: 'invitations', label: 'Invitations',     badge: invitationsSentCount || undefined },
    { key: 'applications', label: 'Applications'    },
  ];

  const ModalField = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: '13px', color: C.text, lineHeight: 1.5 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '22px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Admin Portal</h1>
          <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Manage user accounts, waitlist signups, and invitations</p>
        </div>
        <button style={btnPrimary} onClick={() => setShowInviteModal(true)}>+ Invite User</button>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: '3px', marginBottom: '18px', flexWrap: 'wrap', borderBottom: `1px solid ${C.border}`, paddingBottom: '0' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '7px 14px', fontSize: '12px', cursor: 'pointer', border: 'none',
            background: activeTab === t.key ? C.navy : 'transparent',
            color: activeTab === t.key ? C.white : C.muted,
            fontFamily: 'system-ui', fontWeight: activeTab === t.key ? 600 : 400,
            borderRadius: '3px 3px 0 0', marginBottom: '-1px',
            borderBottom: activeTab === t.key ? `2px solid ${C.navy}` : '2px solid transparent',
            transition: 'all 0.1s',
          }}>
            {t.label}
            {t.badge !== undefined && <span style={{ marginLeft: '6px', background: activeTab === t.key ? C.gold : C.border, color: activeTab === t.key ? C.navy : C.muted, fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '2px' }}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {activeTab === 'applications' && <ApplicationsTabs />}

      {/* ── User list ── */}
      {!['waitlist','invitations','applications'].includes(activeTab) && (
        <div style={card}>
          {loading ? <div style={{ padding: '32px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>Loading...</div>
          : users.length === 0 ? <div style={{ padding: '32px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>No users in this category.</div>
          : users.map((u, i) => (
            <div key={u.id} onClick={() => openUserModal(u)} style={{
              display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px',
              borderBottom: i < users.length - 1 ? `1px solid ${C.borderLt}` : 'none',
              cursor: 'pointer', transition: 'background 0.1s',
            }}
              onMouseEnter={e => (e.currentTarget.style.background = C.surface)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ width: '32px', height: '32px', borderRadius: '3px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: C.gold, flexShrink: 0, fontFamily: 'Georgia, serif' }}>
                {u.full_name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{u.full_name}</div>
                <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                  {u.email} · {u.role === 'manager' ? 'Manager' : 'Allocator'}
                  {u.firm && <> · {u.firm.name}</>}
                  {u.submitted_at && <> · {fmt(u.submitted_at)}</>}
                </div>
              </div>
              <span style={statusBadge(u.account_status)}>{u.account_status.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Waitlist ── */}
      {activeTab === 'waitlist' && (
        <div style={card}>
          {loading ? <div style={{ padding: '32px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>Loading...</div>
          : waitlist.length === 0 ? <div style={{ padding: '32px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>No waitlist entries yet.</div>
          : waitlist.map((w, i) => (
            <div key={w.id} onClick={() => openWaitlistModal(w)} style={{
              display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px',
              borderBottom: i < waitlist.length - 1 ? `1px solid ${C.borderLt}` : 'none',
              cursor: 'pointer', transition: 'background 0.1s',
            }}
              onMouseEnter={e => (e.currentTarget.style.background = C.surface)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ width: '32px', height: '32px', borderRadius: '3px', background: '#EFF8FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: C.teal, flexShrink: 0 }}>✉</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{w.email}</div>
                <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                  {w.role_interest ? (w.role_interest === 'manager' ? 'Manager' : 'Allocator') : 'No role specified'}
                  {w.firm_name && <> · {w.firm_name}</>}
                  {' · '}{w.source} · {fmt(w.created_at)}
                </div>
              </div>
              <span style={statusBadge(w.status)}>{w.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Invitations ── */}
      {activeTab === 'invitations' && (
        <div style={card}>
          {loading ? <div style={{ padding: '32px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>Loading...</div>
          : invitations.length === 0 ? <div style={{ padding: '32px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>No invitations sent yet. Click "+ Invite User" to send your first invitation.</div>
          : invitations.map((inv, i) => (
            <div key={inv.id} onClick={() => setSelectedInvitation(inv)} style={{
              display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px',
              borderBottom: i < invitations.length - 1 ? `1px solid ${C.borderLt}` : 'none',
              cursor: 'pointer', transition: 'background 0.1s',
            }}
              onMouseEnter={e => (e.currentTarget.style.background = C.surface)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ width: '32px', height: '32px', borderRadius: '3px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: '#1D4ED8', flexShrink: 0 }}>→</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{inv.email}</div>
                <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                  {inv.role_intended === 'manager' ? 'Fund Manager' : 'Allocator'}
                  {inv.firm_name_suggested && <> · {inv.firm_name_suggested}</>}
                  {' · Invited by '}{inv.inviter?.full_name || 'Unknown'}
                  {' · '}{fmt(inv.sent_at)}
                </div>
              </div>
              <span style={statusBadge(inv.status)}>{inv.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── User modal ── */}
      {selectedUser && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '24px', width: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '12px', marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>{selectedUser.full_name}</div>
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>{selectedUser.email} · {selectedUser.role === 'manager' ? 'Manager' : 'Allocator'}</div>
              </div>
              <span style={statusBadge(selectedUser.account_status)}>{selectedUser.account_status.replace('_', ' ')}</span>
            </div>
            {selectedUser.firm && (
              <ModalField label="Firm" value={<>
                <div style={{ fontWeight: 600 }}>{selectedUser.firm.name}</div>
                <div>{selectedUser.firm.type} · {selectedUser.firm.headquarters}</div>
                {selectedUser.firm.aum_min != null && <div>AUM: ${selectedUser.firm.aum_min}mm – ${selectedUser.firm.aum_max}mm</div>}
                {selectedUser.firm.website && <a href={selectedUser.firm.website.startsWith('http') ? selectedUser.firm.website : `https://${selectedUser.firm.website}`} target="_blank" rel="noopener noreferrer" style={{ color: C.teal }}>{selectedUser.firm.website}</a>}
                {selectedUser.firm.description && <div style={{ color: C.muted, marginTop: '4px' }}>{selectedUser.firm.description}</div>}
              </>} />
            )}
            {selectedUser.submitted_at && <ModalField label="Submitted" value={fmtFull(selectedUser.submitted_at)} />}
            {selectedUser.reviewed_at && <ModalField label="Last reviewed" value={fmtFull(selectedUser.reviewed_at)} />}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Admin notes (private)</label>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="e.g. Verified FCA reg on 23 May." style={textareaStyle} onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
            </div>
            {['pending_review','rejected'].includes(selectedUser.account_status) && (
              <div style={{ marginBottom: '12px' }}>
                <label style={labelStyle}>Rejection reason (shown to user)</label>
                <textarea value={editRejectionReason} onChange={e => setEditRejectionReason(e.target.value)} placeholder="e.g. Unable to verify firm registration." style={textareaStyle} onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {selectedUser.account_status === 'pending_review' && <>
                <button style={btnPrimary} onClick={handleApprove} disabled={actionLoading}>{actionLoading ? '...' : 'Approve'}</button>
                <button style={btnDanger} onClick={handleReject} disabled={actionLoading}>{actionLoading ? '...' : 'Reject'}</button>
              </>}
              {selectedUser.account_status === 'approved' && !selectedUser.is_super_admin && <button style={btnDanger} onClick={handleSuspend} disabled={actionLoading}>Suspend</button>}
              {['rejected','suspended'].includes(selectedUser.account_status) && <button style={btnPrimary} onClick={handleReinstate} disabled={actionLoading}>Reinstate</button>}
              <button style={btnSecondary} onClick={handleSaveNotes} disabled={actionLoading}>Save Notes</button>
              <button style={btnSecondary} onClick={closeModal} disabled={actionLoading}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Waitlist modal ── */}
      {selectedWaitlist && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '24px', width: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '12px', marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>{selectedWaitlist.email}</div>
              <span style={statusBadge(selectedWaitlist.status)}>{selectedWaitlist.status}</span>
            </div>
            <ModalField label="Role interest" value={selectedWaitlist.role_interest === 'manager' ? 'Fund Manager' : selectedWaitlist.role_interest === 'allocator' ? 'Allocator' : 'Not specified'} />
            {selectedWaitlist.firm_name && <ModalField label="Firm" value={selectedWaitlist.firm_name} />}
            {selectedWaitlist.message && <ModalField label="Message" value={selectedWaitlist.message} />}
            <ModalField label="Source" value={selectedWaitlist.source} />
            <ModalField label="Submitted" value={fmtFull(selectedWaitlist.created_at)} />
            {selectedWaitlist.invited_at && <ModalField label="Invited at" value={fmtFull(selectedWaitlist.invited_at)} />}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Notes (private)</label>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} style={textareaStyle} onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {selectedWaitlist.status === 'pending' && <>
                <button style={btnPrimary} onClick={() => handleSendInvitation(selectedWaitlist)} disabled={actionLoading}>{actionLoading ? '...' : 'Send Invitation'}</button>
                <button style={btnSecondary} onClick={handleMarkInvited} disabled={actionLoading}>Mark Invited</button>
                <button style={btnDanger} onClick={handleMarkDeclined} disabled={actionLoading}>Decline</button>
              </>}
              {['invited','declined'].includes(selectedWaitlist.status) && <button style={btnSecondary} onClick={handleReopenWaitlist} disabled={actionLoading}>Reopen</button>}
              <button style={btnSecondary} onClick={handleSaveWaitlistNotes} disabled={actionLoading}>Save Notes</button>
              <button style={btnDanger} onClick={handleDeleteWaitlist} disabled={actionLoading}>Delete</button>
              <button style={btnSecondary} onClick={closeModal} disabled={actionLoading}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invitation detail modal ── */}
      {selectedInvitation && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '24px', width: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '12px', marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>{selectedInvitation.email}</div>
              <span style={statusBadge(selectedInvitation.status)}>{selectedInvitation.status}</span>
            </div>
            <ModalField label="Role" value={selectedInvitation.role_intended === 'manager' ? 'Fund Manager' : 'Allocator'} />
            {selectedInvitation.firm_name_suggested && <ModalField label="Suggested firm" value={selectedInvitation.firm_name_suggested} />}
            {selectedInvitation.custom_message && <ModalField label="Personal message" value={selectedInvitation.custom_message} />}
            <ModalField label="Invited by" value={selectedInvitation.inviter?.full_name || 'Unknown'} />
            <ModalField label="Sent" value={fmtFull(selectedInvitation.sent_at)} />
            {selectedInvitation.opened_at && <ModalField label="Opened" value={fmtFull(selectedInvitation.opened_at)} />}
            {selectedInvitation.accepted_at && <ModalField label="Accepted" value={fmtFull(selectedInvitation.accepted_at)} />}
            <ModalField label="Invite link" value={<span style={{ fontSize: '11px', color: C.teal, wordBreak: 'break-all', fontFamily: '"Courier New", monospace' }}>https://app.altveno.com/invite/{selectedInvitation.token}</span>} />
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
              {['sent','opened'].includes(selectedInvitation.status) && <>
                <button style={btnPrimary} onClick={handleResendInvitation} disabled={actionLoading}>{actionLoading ? '...' : 'Resend Email'}</button>
                <button style={btnSecondary} onClick={() => copyInviteUrl(selectedInvitation.token)}>Copy URL</button>
                <button style={btnDanger} onClick={handleRevokeInvitation} disabled={actionLoading}>Revoke</button>
              </>}
              {['revoked','expired'].includes(selectedInvitation.status) && <button style={btnPrimary} onClick={handleResendInvitation} disabled={actionLoading}>Resend (new invite)</button>}
              <button style={btnSecondary} onClick={closeModal} disabled={actionLoading}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Send invitation modal ── */}
      {showInviteModal && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '24px', width: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '12px', marginBottom: '18px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '4px' }}>New Invitation</div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>Invite a User to Altveno</div>
            </div>
            <label style={labelStyle}>Email *</label>
            <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="recipient@firm.com" style={inputStyle} autoFocus onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
            <label style={labelStyle}>Role *</label>
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value as any)} style={{ ...inputStyle }}>
              <option value="manager">Fund Manager</option>
              <option value="allocator">Allocator</option>
            </select>
            <label style={labelStyle}>Suggested firm (optional)</label>
            <input type="text" value={inviteFirmName} onChange={e => setInviteFirmName(e.target.value)} placeholder="e.g. Acme Capital" style={inputStyle} onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
            <label style={labelStyle}>Personal message (optional)</label>
            <textarea value={inviteMessage} onChange={e => setInviteMessage(e.target.value)} placeholder="e.g. Hi Alex, great to meet you at the conference..." style={textareaStyle} onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
            {inviteResult && (
              <div style={{ padding: '10px 12px', borderRadius: '3px', marginBottom: '14px', fontSize: '12.5px', background: inviteResult.success ? '#F0FDF4' : '#FEF2F2', color: inviteResult.success ? C.success : C.danger, border: `1px solid ${inviteResult.success ? '#BBF7D0' : '#FECACA'}` }}>
                {inviteResult.message}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...btnPrimary, opacity: (actionLoading || !inviteEmail) ? 0.5 : 1 }} onClick={() => handleSendInvitation()} disabled={actionLoading || !inviteEmail}>
                {actionLoading ? 'Sending...' : 'Send Invitation'}
              </button>
              <button style={btnSecondary} onClick={closeModal} disabled={actionLoading}>{inviteResult?.success ? 'Done' : 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// == Applications sub-tabs ====================================================
// Manager applications keep their own richer flow (regulatory check, contract,
// invoice); the other three share one simpler review and approval screen.

const APPLICATION_KINDS = [
  { key: 'manager',   label: 'Manager' },
  { key: 'allocator', label: 'Allocator' },
  { key: 'provider',  label: 'Service Provider' },
  { key: 'candidate', label: 'Candidate' },
] as const;

type ApplicationKindKey = typeof APPLICATION_KINDS[number]['key'];

function ApplicationsTabs() {
  const [kind, setKind] = useState<ApplicationKindKey>('manager');

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {APPLICATION_KINDS.map(k => {
          const on = kind === k.key;
          return (
            <button key={k.key} onClick={() => setKind(k.key)} style={{
              padding: '6px 14px', borderRadius: '3px', fontSize: '12px',
              cursor: 'pointer', fontFamily: 'system-ui', fontWeight: on ? 600 : 500,
              border: `1px solid ${on ? C.navy : C.border}`,
              background: on ? C.navy : C.white,
              color: on ? C.white : C.textSec,
            }}>{k.label}</button>
          );
        })}
      </div>

      {kind === 'manager'
        ? <AdminApplications />
        : <AdminOnboardingApplications kind={kind} />}
    </div>
  );
}
